import { Command, CommandDialog, CommandInput } from '@/components/ui/command';
import { useProductSearch } from '@/hooks/useProductSearch';
import { SearchIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { useEffect, useState } from 'react';
import { SearchResults } from './search/SearchResults';
import { getRecentSearches } from './search/searchUtils';
import { SortOption } from './search/SortMenu';
import { Button } from './ui/button';

interface SearchCommandProps {
  sortBy?: SortOption;
  showInHeader?: boolean;
}

export function SearchCommand({
  sortBy = 'name-asc',
  showInHeader = false,
}: SearchCommandProps) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const [prescriptionFilter, setPrescriptionFilter] = useState<boolean | null>(
    null
  );
  const [recentSearches, setRecentSearches] = useState<string[]>([]);

  const { products, isLoading, prescriptionCount, otcCount } = useProductSearch(
    value,
    prescriptionFilter,
    sortBy
  );

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'f' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen(open => !open);
      }
    };
    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, []);

  useEffect(() => {
    if (open) {
      setRecentSearches(getRecentSearches());
    }
  }, [open]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const handleSearchSelect = (query: string) => {
    setValue(query);
    setPrescriptionFilter(null);
  };

  return (
    <>
      <div className="flex flex-col md:w-[500px] w-full">
        <div className="relative flex flex-1 items-center w-full border rounded-full bg-[var(--dispensary-gray-50)] pl-3 pr-1 py-1">
          <HugeiconsIcon
            icon={SearchIcon}
            size={16}
            className="text-muted-foreground mr-2"
          />
          <Button
            variant="ghost"
            className="relative flex-1 justify-start h-auto p-0 bodysmall font-normal text-muted-foreground shadow-none bg-transparent hover:bg-transparent hover:text-muted-foreground"
            onClick={() => setOpen(true)}
          >
            <span className="hidden lg:inline-flex">Search medications...</span>
            <span className="inline-flex lg:hidden">Search...</span>
          </Button>
          <div className="pointer-events-none flex items-center gap-1 rounded-full bg-background px-1.5 font-mono text-[10px] opacity-60 sm:flex ml-2">
            <span className="text-xl">⌘</span>F
          </div>
        </div>
      </div>
      <CommandDialog open={open} onOpenChange={setOpen}>
        <Command
          className="rounded-xl border shadow-md"
          onKeyDown={handleKeyDown}
          shouldFilter={false}
        >
          <CommandInput
            placeholder="Type to search..."
            value={value}
            onValueChange={setValue}
            aria-label="Search medications"
          />
          <SearchResults
            value={value}
            filteredProducts={products}
            recentSearches={recentSearches}
            prescriptionCount={prescriptionCount}
            otcCount={otcCount}
            setPrescriptionFilter={setPrescriptionFilter}
            setOpen={setOpen}
            isLoading={isLoading}
            onSearchSelect={handleSearchSelect}
          />
        </Command>
      </CommandDialog>
    </>
  );
}
